import {
  type CharacterSummary,
  CLASSES,
  isGuestAccount,
  type SignInProvider,
  lookTints,
  MAX_CHARACTERS_PER_ACCOUNT,
  NICKNAME_MAX,
  NICKNAME_MIN,
} from '@shefira/shared';

import { type DomAtlas, loadCharacterAtlas } from '../assets/dom-atlas.js';
import {
  clearAccount,
  createCharacter,
  deleteAccount,
  deleteCharacter,
  listRoster,
  selectCharacter,
} from '../net/account.js';
import { AuthError, clearSession, type StoredSession } from '../net/session.js';
import { PROVIDER_NAMES } from '../net/sign-in.js';
import { versionLine } from './changelog-modal.js';
import { showCustomise } from './customise.js';
import { confirmAccountDeletion, confirmCharacterDeletion } from './delete-character-modal.js';
import { DollPreview } from './doll-preview.js';
import { confirmGuestSignOut } from './guest-reminder.js';
import { element, kitChip, kitSeal, kitStage } from './kit.js';
import { uiFormat, uiHtml, uiText } from './text.js';
import './front.css';

/**
 * The account's characters, each drawn as the game draws it, and a way to
 * make another. This is the home screen: where a session starts, and where
 * the in-game "home" button comes back to.
 *
 * It works from either token. An account session (after a login) and a
 * character session (a guest, or a player already in the game) both name an
 * account on the server, so a guest sees their roster like anyone else.
 */
export interface RosterAuth {
  token: string;
  /** Character currently played, to mark it; undefined on the home screen. */
  currentId?: string;
}

/**
 * One roster card: the character dressed as it is, with its own hair and
 * eyes, standing in the meadow like on the profile; under it the level's
 * seal, the name and the class. The one being played wears a ribbon.
 */
export function characterCard(
  character: CharacterSummary,
  atlas: DomAtlas | null,
  current: boolean,
  onDelete?: () => void,
): HTMLLIElement {
  const card = document.createElement('li');
  card.className = 'roster__card';
  if (current) card.classList.add('roster__card--current');
  card.tabIndex = 0;

  const stage = kitStage('roster__stage');
  const prefix = atlas?.prefixOf(`body.${character.classId}`) ?? null;
  if (atlas !== null && prefix !== null) {
    const preview = new DollPreview({
      bodyKey: prefix,
      frame: atlas.frame,
      featureTints: lookTints(character.look),
      hairStyle: character.look.hairStyle,
      body: character.look.body,
      scale: 3,
    });
    // The summary carries template ids; the preview wants instances.
    preview.draw(
      Object.fromEntries(
        Object.entries(character.appearance).map(([slot, templateId]) => [
          slot,
          { id: slot, templateId, quantity: 1, upgradeLevel: 0, bound: false },
        ]),
      ),
    );
    stage.append(preview.canvas);
  } else {
    stage.append(element('span', 'roster__empty', '?'));
  }
  if (current) stage.append(element('span', 'roster__ribbon', 'En jeu'));
  // Never on the character being played: its room holds it.
  if (onDelete !== undefined && !current) {
    const remove = element('button', 'roster__delete', 'Supprimer');
    remove.type = 'button';
    remove.title = uiFormat('Supprimer {name}', { name: character.name });
    remove.setAttribute('aria-label', remove.title);
    remove.addEventListener('click', (event) => {
      event.stopPropagation();
      onDelete();
    });
    remove.addEventListener('keydown', (event) => {
      event.stopPropagation();
    });
    stage.append(remove);
  }

  const info = element('div', 'roster__info');
  const names = element('div', 'roster__names');
  names.append(
    element('strong', 'roster__name', character.name),
    element('span', 'roster__detail', CLASSES[character.classId].name),
  );
  info.append(kitSeal(character.level), names);
  card.append(stage, info);
  return card;
}

/** A free place on the roster: a click puts the cursor in the name field. */
function newCard(onPick: () => void): HTMLLIElement {
  const card = document.createElement('li');
  card.className = 'roster__card roster__card--new';
  card.tabIndex = 0;
  const stage = element('div', 'roster__stage roster__stage--new');
  stage.append(element('span', 'roster__plus', '+'));
  card.append(stage, element('span', 'roster__new-label', 'Nouveau personnage'));
  card.addEventListener('click', onPick);
  card.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') onPick();
  });
  return card;
}

export async function showRoster(auth: RosterAuth): Promise<StoredSession> {
  const atlas = await loadCharacterAtlas();
  let email: string | null = null;
  let providers: SignInProvider[] = [];
  let characters: CharacterSummary[] = [];
  let unreachable: string | null = null;
  try {
    const roster = await listRoster(auth.token);
    email = roster.email;
    providers = roster.providers;
    characters = roster.characters;
  } catch (cause: unknown) {
    unreachable = cause instanceof AuthError ? cause.message : 'Serveur injoignable';
  }

  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'login';
    overlay.innerHTML = uiHtml(`
      <div class="login__stack">
        <div class="login__brand"><img class="login__logo login__logo--small" src="/assets/ui/logo.png" alt="Shefira" width="168" height="68" /><img class="login__beta" src="/assets/ui/beta.png" alt="Bêta" width="73" height="29" /></div>
        <div class="login__panel login__panel--roster">
          <header class="roster__header">
            <h1 class="login__title">Tes personnages</h1>
            <span class="roster__count"></span>
          </header>
          <div class="roster__account"></div>
          <ul class="roster"></ul>
          <form class="login__form roster__form" novalidate>
            <input class="login__input" name="nickname" autocomplete="off" autocapitalize="off"
                   spellcheck="false" minlength="${String(NICKNAME_MIN)}" maxlength="${String(NICKNAME_MAX)}"
                   placeholder="Nom du nouveau personnage" aria-label="Nom du nouveau personnage" />
            <button class="login__submit login__submit--gold" type="submit">Créer</button>
          </form>
          <p class="login__error" role="alert" hidden></p>
          <button class="login__link roster__sign-out" type="button">Se déconnecter</button>
          <button class="login__link roster__delete-account" type="button">Supprimer mon compte</button>
        </div>
      </div>
    `);
    overlay.querySelector('.login__brand')?.after(versionLine());
    document.body.append(overlay);

    const find = (selector: string): HTMLElement => {
      const found = overlay.querySelector<HTMLElement>(selector);
      if (found === null) throw new Error(`Roster markup is missing ${selector}`);
      return found;
    };
    const roster = find('.roster');
    const count = find('.roster__count');
    // Who is signed in, or a guest's reminder that the account is not saved yet.
    find('.roster__account').append(
      isGuestAccount({ email, providers })
        ? element(
            'p',
            'login__hint roster__guest',
            'Compte invité : enregistre-le depuis le jeu pour le retrouver ailleurs.',
          )
        : kitChip({
            icon: 'account',
            label: 'Compte',
            value: email ?? providers.map((provider) => PROVIDER_NAMES[provider]).join(', '),
          }),
    );
    const form = find('.login__form') as HTMLFormElement;
    const error = find('.login__error');
    const submit = form.querySelector('button');

    const fail = (cause: unknown): void => {
      error.textContent = uiText(cause instanceof AuthError ? cause.message : 'Action impossible');
      error.hidden = false;
      if (submit !== null) submit.disabled = false;
    };
    if (unreachable !== null) fail(new AuthError(unreachable, 'unreachable'));

    const play = (character: CharacterSummary): void => {
      error.hidden = true;
      selectCharacter(auth.token, character.id)
        .then((session) => {
          overlay.remove();
          resolve(session);
        })
        .catch(fail);
    };

    const render = (): void => {
      roster.replaceChildren();
      for (const character of characters) {
        const card = characterCard(character, atlas, character.id === auth.currentId, () => {
          confirmCharacterDeletion(character, (name) =>
            deleteCharacter(auth.token, character.id, name),
          )
            .then((deleted) => {
              if (!deleted) return;
              characters = characters.filter((other) => other.id !== character.id);
              render();
            })
            .catch(fail);
        });
        card.addEventListener('click', () => {
          play(character);
        });
        card.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') play(character);
        });
        roster.append(card);
      }
      // One free place is enough to say there is room; the count says how much.
      if (characters.length < MAX_CHARACTERS_PER_ACCOUNT) {
        const nameField = form.querySelector('input');
        roster.append(
          newCard(() => {
            nameField?.focus();
          }),
        );
      }
      count.textContent = `${String(characters.length)} / ${String(MAX_CHARACTERS_PER_ACCOUNT)}`;
      form.hidden = characters.length >= MAX_CHARACTERS_PER_ACCOUNT;
    };
    render();

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      error.hidden = true;
      const nickname = new FormData(form).get('nickname');
      const nicknameStr = typeof nickname === 'string' ? nickname.trim() : '';
      if (submit !== null) submit.disabled = true;
      // The class and the look are chosen with the character in view, on the
      // customisation screen; the roster only asks for the name.
      overlay.hidden = true;
      showCustomise(nicknameStr || 'Nouveau personnage')
        .then((choice) =>
          choice === null
            ? null
            : createCharacter(auth.token, nicknameStr, choice.classId, choice.look),
        )
        .then((created) => {
          if (created === null) return;
          characters = [...characters, created];
          form.reset();
          render();
        })
        .catch(fail)
        .finally(() => {
          overlay.hidden = false;
          if (submit !== null) submit.disabled = false;
        });
    });

    find('.roster__sign-out').addEventListener('click', () => {
      void signOut();
    });
    // Required of any app where an account can be made (ADR 0075, App Store rule 5.1.1(v)).
    find('.roster__delete-account').addEventListener('click', () => {
      error.hidden = true;
      confirmAccountDeletion(() => deleteAccount(auth.token))
        .then((deleted) => {
          if (!deleted) return;
          clearAccount();
          clearSession();
          window.location.reload();
        })
        .catch(fail);
    });
    const signOut = async (): Promise<void> => {
      if (isGuestAccount({ email, providers }) && !(await confirmGuestSignOut())) return;
      clearAccount();
      clearSession();
      window.location.reload();
    };
  });
}
